import { useEffect, useMemo, useRef, useState } from "react";
import { formatPhone, formatDate, formatDateTime } from "@/lib/utils";
import {
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  Crown,
  Check,
  Copy,
  DollarSign,
  Clock,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  Trophy,
  X,
  type LucideIcon,
} from "lucide-react";

import { api, assetUrl } from "@/lib/api";
import { EXPERIENCE_LABELS, buyerStatusLabel, describeBuyerProfile } from "@/lib/buyerProfile";
import { localTimeIn, safeTimeZone, timeZoneLabel } from "@/lib/userLocation";
import BuyBoxPanel from "@/components/BuyBoxPanel";
import Loader from "@/components/ui/loader";

interface LookupItem { id: number; name: string }

interface BuyerProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  created_at: string;
  intros_count: number;
  plan_name: string | null;
  plan_amount: number | null;
  subscription_status: string | null;
  subscription_started_at: string | null;
  // Null for a healthy Stripe subscription — Stripe owns the period end and it
  // is not persisted, so that case reads "Auto-renews" rather than a blank.
  subscription_ends_at: string | null;
  cancel_at_period_end: boolean;
  is_stripe: boolean;
  last_login_at: string | null;
  last_activity_at: string | null;
  last_nda_signed_at: string | null;
  // Same flags as the interests feed's chips (buyer_is_premium / nda_signed).
  is_premium?: boolean;
  nda_signed?: boolean;
  // Location (TKT-00157): browser timezone + the home state the buyer gave.
  timezone?: string | null;
  home_state?: string | null;
  home_state_timezone?: string | null;
  promo_code: string | null;
  promo_expires_at: string | null;
  promo_expired: boolean;
  preferences_completed: boolean;
  experience: string | null;
  budget_min: number | null;
  budget_max: number | null;
  category_ids: number[];
  other_category: string | null;
  years_in_business: string | null;
  funding_methods: string[];
  prequalifications?: string[];
  additional_notes: string | null;
  preferred_location: string | null;
  preferred_country_names?: string[];
  preferred_state_names?: string[];
  preferred_city_labels?: string[];
  preferred_city_names?: string[];
  sde_preference: string | null;
  account_status?: string | null;
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-xs font-bold tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1.5 break-words rounded-lg border border-border/80 bg-background/50 px-3.5 py-2.5 text-sm font-semibold text-foreground">
        {value || <span className="font-normal italic text-muted-foreground/60">Not set</span>}
      </div>
    </div>
  );
}

function CopyEmailButton({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard blocked (insecure context / permissions) — the address is
         still fully visible and selectable. */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied" : "Copy email"}
      aria-label={copied ? "Email copied" : "Copy email"}
      className="mt-px shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

// Ordered SBA first, matching the interests page's chip ("SBA + Private Lender").
const PREQUAL_SHORT: Record<string, string> = { sba: "SBA", private_lender: "Private Lender" };

type Tone = "gold" | "sky" | "violet" | "emerald" | "muted";
const TONE: Record<Tone, string> = {
  gold: "border-primary/30 bg-primary/10 text-primary",
  sky: "border-sky-500/30 bg-sky-500/10 text-sky-600",
  violet: "border-violet-500/30 bg-violet-500/10 text-violet-600",
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600",
  // Grey frame, but full-strength ink: "Not yet" / "Not signed" are facts
  // staff need to read, not disabled text.
  muted: "border-border/70 bg-muted/30 text-foreground/75",
};

function KpiTile({ icon: Icon, label, value, sub, tone }: {
  icon: LucideIcon; label: string; value: string; sub?: string | null; tone: Tone;
}) {
  return (
    <div className={`min-w-0 rounded-xl border px-3.5 py-3 ${TONE[tone]}`}>
      <div className="flex items-center gap-1.5 text-[0.62rem] font-bold uppercase tracking-[0.14em]">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1.5 text-sm font-bold leading-tight text-foreground">
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[0.68rem] opacity-80">{sub}</div>}
    </div>
  );
}

/**
 * The buyer-quality labels staff see as chips on the Interests page — premium
 * plan, pre-qualification, buying experience, NDA — as KPI tiles, so the
 * profile popup tells the same story as the row it was opened from. Each tile
 * keeps its chip's icon and colour when the label applies, and goes muted when
 * it doesn't, so a gap is as visible as a strength.
 */
function BuyerKpiTiles({ profile }: { profile: BuyerProfile }) {
  const stored = profile.prequalifications ?? [];
  const prequal = [
    ...Object.keys(PREQUAL_SHORT).filter((k) => stored.includes(k)).map((k) => PREQUAL_SHORT[k]),
    ...stored.filter((p) => !(p in PREQUAL_SHORT)),
  ];
  const experienced = profile.experience === "bought_1_2" || profile.experience === "bought_3_plus";
  // A signature on an older NDA version doesn't count (a newer NDA invalidates
  // it), so that buyer reads "Needs re-sign" rather than a bare "Not signed".
  const ndaSub = profile.nda_signed
    ? formatDate(profile.last_nda_signed_at)
    : profile.last_nda_signed_at
    ? "Signed an older version — needs re-sign"
    : null;

  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <KpiTile
        icon={Crown}
        label={profile.is_premium ? "Premium buyer" : "Plan"}
        value={profile.plan_name ?? "Basic"}
        sub={profile.is_premium ? "Top tier plan" : profile.plan_name ? null : "Free — no paid plan"}
        tone={profile.is_premium ? "gold" : "muted"}
      />
      <KpiTile
        icon={BadgeCheck}
        label="Pre-Qualified"
        value={prequal.length ? prequal.join(" + ") : "Not yet"}
        sub={prequal.length ? "Lender pre-qualified" : null}
        tone={prequal.length ? "sky" : "muted"}
      />
      <KpiTile
        icon={Trophy}
        label="Experience"
        value={profile.experience ? EXPERIENCE_LABELS[profile.experience] ?? profile.experience : "Not set"}
        tone={experienced ? "violet" : "muted"}
      />
      <KpiTile
        icon={ShieldCheck}
        label="NDA"
        value={profile.nda_signed ? "Signed" : "Not signed"}
        sub={ndaSub}
        tone={profile.nda_signed ? "emerald" : "muted"}
      />
    </div>
  );
}

/**
 * Where the buyer is and what time it is there, so staff can call at a sensible
 * hour (TKT-00157). The browser timezone wins for the clock — it follows the
 * buyer if they move or travel — and the home state names the place. When the
 * two disagree, both show.
 */
function BuyerLocalTime({ profile }: { profile: BuyerProfile }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const browserTz = safeTimeZone(profile.timezone);
  const stateTz = safeTimeZone(profile.home_state_timezone);
  const tz = browserTz ?? stateTz;
  const state = profile.home_state;

  if (!tz && !state) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        <span>Location not provided</span>
      </div>
    );
  }

  // The home state reads with its own zone ("Texas · Central Time"); the
  // browser's zone gets its own line when it differs, and drives the clock.
  const label = state && stateTz ? timeZoneLabel(stateTz) : tz ? timeZoneLabel(tz) : null;
  const elsewhere = browserTz && stateTz && timeZoneLabel(browserTz) !== timeZoneLabel(stateTz);
  return (
    <>
      <div className="flex items-start gap-2 text-sm text-muted-foreground">
        <MapPin className="mt-[3px] h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0">
          {state && <span className="font-semibold text-foreground">{state}</span>}
          {state && label && " · "}
          {label}
          {elsewhere && <span className="block text-[0.7rem]">Currently browsing from {timeZoneLabel(browserTz!)}</span>}
        </span>
      </div>
      {tz && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span>
            <span className="font-semibold text-foreground">{localTimeIn(tz, now)}</span> their time
          </span>
        </div>
      )}
    </>
  );
}

function StrengthBar({ label, pct, hint }: { label: string; pct: number; hint?: string | null }) {
  const barColor =
    pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-primary" : pct > 0 ? "bg-amber-500" : "bg-rose-400";
  const textColor =
    pct >= 80 ? "text-emerald-600" : pct >= 50 ? "text-primary" : pct > 0 ? "text-amber-600" : "text-rose-500";
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-muted-foreground">{label}</span>
        <span className={`font-bold ${textColor}`}>{pct}%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      {hint && <div className="mt-1 text-[0.68rem] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function BuyerProfileModal({
  buyerId,
  onClose,
  buyerIds,
  onNavigate,
}: {
  buyerId: string;
  onClose: () => void;
  /** The buyers listed on the page that opened the popup, in on-screen order.
   *  With onNavigate, the header gets ‹ › (and ←/→) to step through them
   *  without closing the popup. */
  buyerIds?: string[];
  onNavigate?: (buyerId: string) => void;
}) {
  const [profile, setProfile] = useState<BuyerProfile | null>(null);
  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    api.get<LookupItem[]>("/lookups/categories")
      .then((cats) => { if (active) setCategories(cats); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    // `active` drops a slow response for a buyer the admin already stepped past.
    let active = true;
    setLoading(true);
    api.get<BuyerProfile>(`/interests/buyer/${buyerId}/profile`)
      .then((p) => {
        if (!active) return;
        setProfile(p);
        bodyRef.current?.scrollTo({ top: 0 });
      })
      .catch(() => { if (active) setProfile(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [buyerId]);

  const navIndex = buyerIds && onNavigate ? buyerIds.indexOf(buyerId) : -1;
  const prevId = navIndex > 0 ? buyerIds![navIndex - 1] : null;
  const nextId = navIndex >= 0 && navIndex < buyerIds!.length - 1 ? buyerIds![navIndex + 1] : null;

  useEffect(() => {
    if (navIndex < 0) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (e.key === "ArrowLeft" && prevId) onNavigate!(prevId);
      else if (e.key === "ArrowRight" && nextId) onNavigate!(nextId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navIndex, prevId, nextId, onNavigate]);

  // Same derivation as the buyer's own My Profile card (lib/buyerProfile).
  const view = useMemo(() => (profile ? describeBuyerProfile(profile, categories) : null), [profile, categories]);
  const experience = view?.experience ?? null;
  const funding = view?.funding ?? [];
  const prequal = view?.prequal ?? [];

  const fullName = profile ? `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() || "Buyer" : "Buyer";
  const initials = profile
    ? (`${profile.first_name?.[0] ?? ""}${profile.last_name?.[0] ?? ""}`.toUpperCase() || "B")
    : "B";
  const memberSince = profile
    ? new Date(profile.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : "—";
  const subStatus = profile?.subscription_status;
  // A promo / manual grant keeps status "active" forever — nothing flips it when
  // its paid-through date passes, the lapse is computed at read time. plan_name
  // comes from that expiry-aware lookup, so "row says active but there is no
  // tier" means the grant ran out. Labelling that "Active" next to "Basic Plan"
  // would contradict both the plan card and the directory's Sub End column.
  const subLapsed = Boolean(profile && subStatus === "active" && !profile.plan_name);
  const subStatusLabel = subLapsed
    ? "Expired"
    : subStatus === "past_due"
    ? "Past due"
    : subStatus === "canceled"
    ? "Cancelled"
    : subStatus === "active"
    ? "Active"
    : null;
  const subStatusTone =
    subStatus === "active" && !subLapsed
      ? "bg-emerald-500/15 text-emerald-600"
      : subStatus === "past_due"
      ? "bg-amber-500/15 text-amber-600"
      : "bg-rose-500/15 text-rose-600";
  // "Subscription Ends" reads as three different things — a scheduled
  // cancellation, a promo/manual paid-through date, or a Stripe sub that simply
  // keeps renewing. The last one has no date to print (Stripe owns the period
  // end; only cancellations are written back), so it says so rather than "—",
  // which would be indistinguishable from having no plan at all.
  const subEndLabel = !profile?.subscription_started_at
    ? "—"
    : profile.cancel_at_period_end && profile.subscription_ends_at
    ? `Cancels ${formatDate(profile.subscription_ends_at)}`
    : profile.subscription_ends_at
    ? formatDate(profile.subscription_ends_at)
    : profile.is_stripe
    ? "Auto-renews"
    : "—";
  const hasPrefs = Boolean(profile?.preferences_completed);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" />

      <div
        /* max-w-6xl, not 4xl: the app renders at --app-zoom (0.75-0.875 on
           desktop), so a 4xl shell landed under 800 real pixels and squeezed
           the two-column body. 6xl is the width the rest of the app's wide
           surfaces already use. */
        className="relative flex max-h-modal w-full max-w-6xl flex-col overflow-hidden rounded-t-2xl border border-border bg-card shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 sm:px-6 sm:py-5 bg-primary text-primary-foreground shrink-0">
          <div>
            <h2 className="text-xl font-bold sm:text-2xl">Buyer Profile</h2>
            <p className="text-sm text-primary-foreground/70">
              {profile ? `${fullName} · ` : ""}Acquisition criteria &amp; qualification
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3 sm:gap-4">
            {navIndex >= 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => prevId && onNavigate!(prevId)}
                  disabled={!prevId}
                  title="Previous buyer (←)"
                  aria-label="Previous buyer"
                  className="rounded-xl border-2 border-primary-foreground/30 p-2 sm:p-2.5 transition-colors hover:bg-primary-foreground/10 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent"
                >
                  <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
                <span className="min-w-[5.5rem] text-center text-sm font-bold tabular-nums text-primary-foreground sm:text-base">
                  {navIndex + 1} of {buyerIds!.length}
                </span>
                <button
                  type="button"
                  onClick={() => nextId && onNavigate!(nextId)}
                  disabled={!nextId}
                  title="Next buyer (→)"
                  aria-label="Next buyer"
                  className="rounded-xl border-2 border-primary-foreground/30 p-2 sm:p-2.5 transition-colors hover:bg-primary-foreground/10 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent"
                >
                  <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            )}
            <button onClick={onClose} className="text-primary-foreground/70 hover:text-primary-foreground transition-colors" aria-label="Close">
              <X className="h-6 w-6 sm:h-7 sm:w-7" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          aria-busy={loading}
          className={`flex-1 overflow-y-auto bg-muted/20 p-3 transition-opacity sm:p-5${
            loading && profile ? " pointer-events-none opacity-50" : ""
          }`}
        >
          {loading && !profile ? (
            <div className="flex items-center justify-center py-20"><Loader /></div>
          ) : !profile ? (
            <div className="flex flex-col items-center justify-center gap-2 py-20 text-center text-muted-foreground">
              <Sparkles className="h-8 w-8 text-muted-foreground/40" />
              <p className="text-sm">Could not load this buyer's profile.</p>
            </div>
          ) : (
            /* The Buy Box leads: it is the buyer's acquisition target (their
               "customer avatar"), so it takes the wide column and comes first in
               the DOM — on top on mobile. On desktop the identity + account
               stats sidebar is pulled to the left with lg:order-first.
               minmax(0,1fr), never a bare 1fr: a grid track sized `auto` at
               its minimum is pushed out by its widest unbreakable content and
               the modal grows a horizontal scrollbar. */
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
              {/* ══ Main: Buy Box, qualification tiles, financial qualification ══ */}
              <div className="min-w-0 space-y-5">
              {hasPrefs ? (
                <>
                  {view && (
                    <BuyBoxPanel
                      view={view}
                      subtitle={`What ${profile.first_name?.trim() || "this buyer"} is looking to acquire`}
                      sdeConflict="Minimum SDE is above this buyer's max budget — their Buy Box will match almost nothing."
                    />
                  )}
                  <BuyerKpiTiles profile={profile} />

                  <div className="rounded-2xl border border-border/80 bg-card p-5 shadow-sm sm:p-6">
                    <div className="mb-5 flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
                        <DollarSign className="h-4 w-4" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground">Financial Qualification</h3>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Buying Experience" value={experience} />
                      <Field label="Financing Preferences" value={funding.join(", ") || null} />
                      <Field label="Pre-Qualified" value={prequal.join(", ") || null} />
                    </div>

                    {profile.additional_notes && (
                      <div className="mt-4">
                        <div className="text-xs font-bold tracking-wide text-muted-foreground">Additional Notes</div>
                        <p className="mt-1.5 rounded-lg border border-border/80 bg-background/50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground">
                          {profile.additional_notes}
                        </p>
                      </div>
                    )}

                    <div className="mt-5 flex items-center gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
                      <BadgeCheck className="h-5 w-5 shrink-0 text-emerald-600" />
                      <p className="text-sm font-medium text-emerald-700">
                        Buy Box on file — acquisition criteria captured for faster matching.
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                <BuyerKpiTiles profile={profile} />
                <div className="flex min-w-0 flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-border bg-muted/20 px-6 py-16 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Sparkles className="h-7 w-7" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-foreground">No Buy Box on file yet</p>
                    <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                      This buyer hasn't configured their acquisition criteria — industries, geography, budget, or financing.
                    </p>
                  </div>
                </div>
                </>
              )}
              </div>
              {/* ══ Sidebar: identity + strength ══ */}
              <div className="self-start overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm lg:order-first">
                {/* Identity banner. The name lives here rather than under the
                    avatar so the gold band carries the record instead of being
                    dead space. Text is `primary-foreground` (the brand dark ink),
                    matching this modal's own header — white on the #d8b45c→#a9842b
                    gradient would fall well under contrast minimums.
                    Bottom padding is what the avatar overlaps into: it hangs 2.25rem
                    (-bottom-9) below a 5rem circle, so pb-12 keeps the status line
                    clear of it. */}
                <div className="relative bg-gold-gradient px-5 pb-12 pt-5">
                  <div className="text-[0.6rem] font-black uppercase tracking-[0.22em] text-primary-foreground/60">
                    Buyer
                  </div>
                  {/* Company-style names run long; wrap to two lines and clamp
                      rather than truncating mid-word, with the full value on hover. */}
                  <h3
                    title={fullName}
                    className="mt-1.5 line-clamp-2 break-words font-display text-2xl font-bold leading-[1.15] text-primary-foreground"
                  >
                    {fullName}
                  </h3>
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-foreground/45" />
                    <span className="text-xs font-bold text-primary-foreground/75">{buyerStatusLabel(profile.account_status)}</span>
                  </div>

                  <div className="absolute -bottom-9 left-5 h-20 w-20 overflow-hidden rounded-full border-4 border-card bg-primary-foreground shadow-md">
                    {profile.avatar_url ? (
                      <img src={assetUrl(profile.avatar_url)!} alt="Avatar" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center font-display text-2xl font-bold text-primary">
                        {initials}
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-5 pb-5 pt-12">
                  <div className="space-y-1.5">
                    {/* Wraps, never truncates: staff copy this address to pass the
                        buyer on, and an ellipsis hid the domain of long ones. */}
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Mail className="mt-[3px] h-3.5 w-3.5 shrink-0" />
                      <a href={`mailto:${profile.email}`} className="min-w-0 break-all no-underline hover:underline">
                        {profile.email}
                      </a>
                      <CopyEmailButton email={profile.email} />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      <span>{formatPhone(profile.phone) || "Not provided"}</span>
                    </div>
                    <BuyerLocalTime profile={profile} />
                  </div>

                  {profile.plan_name ? (
                    <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-primary/25 bg-primary/10 px-3.5 py-3">
                      <Star className="h-4 w-4 shrink-0 fill-primary text-primary" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase tracking-wide text-primary">{profile.plan_name} Plan</span>
                          {subStatusLabel && (
                            <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide ${subStatusTone}`}>{subStatusLabel}</span>
                          )}
                        </div>
                        {profile.plan_amount != null && (
                          <div className="text-[0.7rem] text-primary/80">${profile.plan_amount.toLocaleString()}/month</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* No paid subscription = the free "Basic" tier. There is no
                       Basic row in `tiers` (it's the absence of a plan), so it's
                       labelled here, styled flat rather than in the paid-plan
                       gold. Any subscription status still rides alongside. */
                    <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-border/70 bg-muted/30 px-3.5 py-3">
                      <Star className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Basic Plan</span>
                          {subStatusLabel && (
                            <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide ${subStatusTone}`}>{subStatusLabel}</span>
                          )}
                        </div>
                        <div className="text-[0.7rem] text-muted-foreground/80">Free — no paid subscription</div>
                      </div>
                    </div>
                  )}

                  <div className="mt-5 space-y-3 border-t border-border/70 pt-4">
                    {[
                      { label: "Intros Requested", value: profile.intros_count },
                      { label: "Member Since", value: memberSince },
                      { label: "Last Login", value: formatDateTime(profile.last_login_at) },
                      { label: "Last Active", value: formatDate(profile.last_activity_at) },
                      { label: "Last NDA Signed", value: formatDate(profile.last_nda_signed_at) },
                      { label: "Subscription Started", value: formatDate(profile.subscription_started_at) },
                      { label: "Subscription Ends", value: subEndLabel },
                      { label: "Promo Code", value: profile.promo_code || "—" },
                      ...(profile.promo_code
                        ? [{
                            label: profile.promo_expired ? "Promo Expired" : "Promo Active Until",
                            value: formatDate(profile.promo_expires_at),
                          }]
                        : []),
                    ].map((s) => (
                      <div key={s.label} className="flex items-start justify-between gap-3 text-sm">
                        <span className="shrink-0 text-muted-foreground">{s.label}</span>
                        <span className="min-w-0 text-right font-bold text-foreground">{s.value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 rounded-xl border border-border/70 bg-muted/30 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wide text-foreground">Profile Strength</span>
                      <span className="text-sm font-extrabold text-primary">{view?.overallPct ?? 0}%</span>
                    </div>
                    <div className="space-y-3">
                      {(view?.strength ?? []).map((s) => (
                        <StrengthBar key={s.label} label={s.label} pct={s.pct} hint={s.hint} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
